import React, {useState} from 'react';
import {Pressable, StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';

import {Icon} from '@/shared/ui/icons';
import {Menu, MenuOption, useAnchor} from '@/shared/ui/menu';
import {AppText} from '@/shared/ui/text';
import {controls, radii, shadows, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Панель фильтров: строка «пилюль» вида «Терминал: Проходная ▾».
 *
 * ## Что это заменило
 *
 * Форму. Подпись слева, группа фишек справа, по строке на фильтр — пять фильтров
 * занимали три ряда над таблицей и отъедали у неё треть окна. Все варианты были
 * нарисованы одновременно, хотя выбирают из них один и меняют его раз в сеанс.
 *
 * Панель — то, что стоит над таблицей в любой системе, которую открывают каждый день:
 * фильтры сжаты до одной строки, значение читается с кнопки, а варианты приезжают в
 * меню по нажатию.
 *
 * ## Правило, ради которого это вообще писалось
 *
 * **Пилюля всегда называет и ЧТО фильтруется, и ЧЕМ.** Не «Все ▾», не значок воронки с
 * числом «3», а «Отдел: Цех». Кнопка с одним числом отвечает на «сколько фильтров
 * включено» и не отвечает на «почему тут пусто» — а это единственный вопрос, ради
 * которого на фильтры смотрят, ничего не меняя.
 *
 * Сужающая пилюля вдобавок КРАШЕНА: заливка брендом здесь значит «этот контрол сейчас
 * что-то прячет». Пилюля в самом широком своём положении («Терминал: Все») остаётся
 * обводкой — она ничего не убирает, и подсвечивать её значило бы кричать о бездействии.
 */
const styles = StyleSheet.create({
  /* Строка панели. Переносится: на 1180pt помещается всё, на 700pt — в два ряда. */
  bar: {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing(2)},

  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    height: controls.md,
    paddingHorizontal: spacing(3),
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    maxWidth: 320,
  },
  /* Палец, а не указатель: на телефоне пилюля — это то, во что надо попасть. */
  pillPhone: {height: controls.lg},
  label: {flexShrink: 0},
  value: {flexShrink: 1, minWidth: 0},

  body: {gap: spacing(3), paddingVertical: spacing(1)},
});

export type FilterOption = {value: string; label: string};

export function FilterBar({
  children,
  style,
  testID,
}: {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  return (
    <View style={[styles.bar, style]} testID={testID}>
      {children}
    </View>
  );
}

/**
 * Пилюля. Сама по себе ничего не открывает — только рисуется и меряется; чем она
 * управляет, решает то, что стоит вокруг неё.
 */
export const FilterPill = React.forwardRef<
  View,
  {
    label: string;
    value: string;
    /** Сужает ли этот фильтр выборку прямо сейчас. */
    active: boolean;
    phone?: boolean;
    onPress: () => void;
    testID?: string;
  }
>(function Pill({label, value, active, phone = false, onPress, testID}, ref) {
  const p = useTheme();

  return (
    <Pressable
      ref={ref}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={`${label}: ${value}`}
      accessibilityState={{expanded: false}}
      onPress={onPress}
      style={state => [
        styles.pill,
        phone && styles.pillPhone,
        shadows.sm,
        {
          backgroundColor: active
            ? p.accent
            : state.pressed || (state as {hovered?: boolean}).hovered
            ? p.muted
            : p.card,
          borderColor: active ? p.primary : p.input,
        },
      ]}>
      <AppText variant="caption" numberOfLines={1} style={styles.label}>
        {label}
      </AppText>
      <AppText
        variant="label"
        color={active ? p.primary : p.foreground}
        numberOfLines={1}
        style={styles.value}>
        {value}
      </AppText>
      <Icon name="chevronDown" size={14} color={p.mutedForeground} />
    </Pressable>
  );
});

/**
 * Выбор одного значения: пилюля плюс меню под ней.
 *
 * `neutral` — то значение, при котором фильтр ничего не убирает. Оно же решает, красить
 * ли пилюлю: «включённость» здесь определяется не тем, что значение задано, а тем, что
 * оно что-то прячет.
 */
export function FilterSelect({
  label,
  value,
  options,
  onChange,
  neutral = '',
  phone = false,
  testID,
}: {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (next: string) => void;
  neutral?: string | null;
  phone?: boolean;
  testID?: string;
}) {
  const [open, setOpen] = useState(false);
  const {ref, anchor, measure} = useAnchor();
  const current = options.find(o => o.value === value);

  return (
    <>
      <FilterPill
        ref={ref}
        label={label}
        value={current?.label ?? '—'}
        active={value !== neutral}
        phone={phone}
        onPress={() => measure(() => setOpen(true))}
        testID={testID}
      />
      <Menu
        visible={open}
        onClose={() => setOpen(false)}
        anchor={anchor}
        title={label}
        testID={testID ? `${testID}-menu` : undefined}>
        {options.map(option => (
          <MenuOption
            key={option.value}
            label={option.label}
            selected={option.value === value}
            onPress={() => {
              onChange(option.value);
              setOpen(false);
            }}
            testID={testID ? `${testID}-option-${option.value || 'any'}` : undefined}
          />
        ))}
      </Menu>
    </>
  );
}

/**
 * Пилюля, за которой не список, а произвольное содержимое: поле ввода, два календаря,
 * что угодно. Всё, что ей нужно от вызывающего, — как назвать текущее состояние.
 */
export function FilterPopover({
  label,
  value,
  active,
  phone = false,
  children,
  minWidth,
  testID,
}: {
  label: string;
  value: string;
  active: boolean;
  phone?: boolean;
  children: React.ReactNode;
  minWidth?: number;
  testID?: string;
}) {
  const [open, setOpen] = useState(false);
  const {ref, anchor, measure} = useAnchor();

  return (
    <>
      <FilterPill
        ref={ref}
        label={label}
        value={value}
        active={active}
        phone={phone}
        onPress={() => measure(() => setOpen(true))}
        testID={testID}
      />
      <Menu
        visible={open}
        onClose={() => setOpen(false)}
        anchor={anchor}
        title={label}
        minWidth={minWidth ?? 260}
        testID={testID ? `${testID}-menu` : undefined}>
        <View style={styles.body}>{children}</View>
      </Menu>
    </>
  );
}
